import Flickity from 'flickity-fade';

type AltaXGen2ImagingDOM = {
	controls: NodeListOf<HTMLElement> | null;
	rotator: HTMLElement | null;
};

type AltaXGen2PayloadsReadyDOM = {
	next: HTMLElement | null;
	prev: HTMLElement | null;
	rotator: HTMLElement | null;
};

type AltaXGen2FlyAnythingTabsDOM = {
	buttons: HTMLElement[];
	panels: HTMLElement[];
	root: HTMLElement | null;
};

type AltaXGen2FlightTimeDOM = {
	chart: SVGSVGElement | null;
	chartAxisLabelsX: HTMLElement[];
	chartAxisX: HTMLElement | null;
	chartPlot: HTMLElement | null;
	chartPoint: HTMLElement | null;
	chartTooltip: HTMLElement | null;
	chartTooltipValue: HTMLElement | null;
	chartWrap: HTMLElement | null;
	flightStatus: HTMLElement | null;
	flightTimeValue: HTMLElement | null;
	payloadSlider: HTMLInputElement | null;
	payloadUnit: HTMLElement | null;
	payloadValue: HTMLElement | null;
	root: HTMLElement | null;
	sliderMaxUnit: HTMLElement | null;
	sliderMaxValue: HTMLElement | null;
	sliderMinUnit: HTMLElement | null;
	sliderMinValue: HTMLElement | null;
	stepDown: HTMLElement | null;
	stepUp: HTMLElement | null;
	unitButtons: HTMLButtonElement[];
};

type AltaXGen2FeaturesDOM = {
	buttons: HTMLButtonElement[];
	captionVideos: HTMLVideoElement[];
	heroVideos: HTMLVideoElement[];
	heroWrap: HTMLElement | null;
	mobileTiles: HTMLElement[];
	overlays: HTMLElement[];
	panels: HTMLElement[];
	root: HTMLElement | null;
	stage: HTMLElement | null;
	stageViewport: HTMLElement | null;
	tabConnector: SVGSVGElement | null;
	tabConnectorPath: SVGPathElement | null;
};

type AltaXGen2TestingDOM = {
	cards: HTMLElement[];
	featureVideo: HTMLVideoElement | null;
	root: HTMLElement | null;
};

type FlickityCarouselInstance = Flickity & {
	next: (isWrap?: boolean) => void;
	on: (event: string, listener: (...args: unknown[]) => void) => void;
	previous: (isWrap?: boolean) => void;
	resize: () => void;
	select: (index: number, isWrapped?: boolean, isInstant?: boolean) => void;
	selectedIndex: number;
	size: { innerWidth: number };
	slidesWidth: number;
};

const bindTablistKeyboard = (buttons: HTMLElement[], activate: (index: number) => void) => {
	buttons.forEach((button, index) => {
		button.addEventListener('keydown', (event) => {
			let next: number | null = null;

			switch (event.key) {
				case 'ArrowRight':
				case 'ArrowDown':
					next = (index + 1) % buttons.length;
					break;
				case 'ArrowLeft':
				case 'ArrowUp':
					next = (index - 1 + buttons.length) % buttons.length;
					break;
				case 'Home':
					next = 0;
					break;
				case 'End':
					next = buttons.length - 1;
					break;
				default:
					return;
			}

			event.preventDefault();
			activate(next);
			buttons[next]?.focus();
		});
	});
};

const syncRovingTabindex = (buttons: HTMLElement[], activeIndex: number) => {
	buttons.forEach((button, index) => {
		button.tabIndex = index === activeIndex ? 0 : -1;
	});
};

class AltaXGen2Imaging {
	currentIndex = 0;
	DOM = {} as AltaXGen2ImagingDOM;
	flkty: FlickityCarouselInstance;

	constructor() {
		this.cache();
		this.initRotator();
		this.events();
	}

	cache() {
		this.DOM = {
			rotator: document.querySelector('.js-altaXGen2Imaging .js-rotator') as HTMLElement | null,
			controls: document.querySelectorAll(
				'.js-altaXGen2Imaging .js-controlButton'
			) as NodeListOf<HTMLElement>,
		};
	}

	initRotator() {
		if (!this.DOM.rotator) {
			return;
		}

		this.flkty = new Flickity(this.DOM.rotator as HTMLElement, {
			pageDots: false,
			prevNextButtons: false,
			fade: true,
			imagesLoaded: true,
		}) as unknown as FlickityCarouselInstance;

		this.flkty.on('change', this.rotatorChange.bind(this));

		const resize = () => {
			this.ensureImagingViewportHeight();
			this.flkty.resize();
		};

		this.ensureImagingViewportHeight();
		this.DOM.rotator.querySelectorAll('img').forEach((img) => {
			if (img.complete) {
				return;
			}

			img.addEventListener('load', resize, { once: true });
		});

		window.addEventListener('load', resize, { once: true });
		requestAnimationFrame(() => requestAnimationFrame(resize));
	}

	ensureImagingViewportHeight() {
		const rotator = this.DOM.rotator as HTMLElement | null;
		if (!rotator) {
			return;
		}

		const viewport = rotator.querySelector('.flickity-viewport') as HTMLElement | null;
		if (!viewport) {
			return;
		}

		const width = rotator.getBoundingClientRect().width;
		if (width < 1) {
			return;
		}

		const targetHeight = Math.round((width * 776) / 740);
		if (viewport.getBoundingClientRect().height < 2) {
			viewport.style.height = `${targetHeight}px`;
		}
	}

	events() {
		const controls = this.DOM.controls as NodeListOf<HTMLElement>;
		if (!controls) {
			return;
		}

		controls.forEach((control) => {
			control.addEventListener('click', this.buttonClick.bind(this));
		});
	}

	buttonClick(e: Event) {
		const target = e.currentTarget as HTMLElement;
		const controls = [...(this.DOM.controls as NodeListOf<HTMLElement>)];
		let index = controls.indexOf(target);
		index = index === this.currentIndex ? 0 : index;
		this.flkty.select(index);
		this.currentIndex = index;
	}

	rotatorChange(index: number) {
		const controls = this.DOM.controls as NodeListOf<HTMLElement>;
		if (!controls) {
			return;
		}

		controls.forEach((control) => {
			control.classList.remove('active');
		});
		controls[index]?.classList.add('active');
		this.currentIndex = index;
	}
}

new AltaXGen2Imaging();

class AltaXGen2PayloadsReady {
	DOM = {} as AltaXGen2PayloadsReadyDOM;
	flkty: FlickityCarouselInstance;

	constructor() {
		this.cache();
		if (!this.DOM.rotator) {
			return;
		}

		this.init();
		this.events();
	}

	cache() {
		const wrapper = document.querySelector('.js-altaXGen2PayloadsReady');
		if (!wrapper) {
			return;
		}

		this.DOM = {
			next: wrapper.querySelector('.js-next') as HTMLElement | null,
			prev: wrapper.querySelector('.js-prev') as HTMLElement | null,
			rotator: wrapper.querySelector('.rotator') as HTMLElement | null,
		};
	}

	init() {
		if (!this.DOM.rotator) {
			return;
		}

		this.flkty = new Flickity(this.DOM.rotator, {
			cellAlign: 'left',
			contain: true,
			// Page by full viewport so we only ever land on whole-card groups.
			groupCells: true,
			imagesLoaded: true,
			pageDots: false,
			percentPosition: true,
			prevNextButtons: false,
			wrapAround: false,
		}) as unknown as FlickityCarouselInstance;

		const syncControls = () => {
			this.updateControls();
			this.flkty.resize();
		};

		this.flkty.on('ready', this.updateControls.bind(this));
		this.flkty.on('resize', this.updateControls.bind(this));
		this.flkty.on('change', this.updateControls.bind(this));

		this.DOM.rotator.querySelectorAll('img').forEach((img) => {
			if (img.complete) {
				return;
			}

			img.addEventListener('load', syncControls, { once: true });
		});

		window.addEventListener('load', syncControls, { once: true });
		requestAnimationFrame(() => requestAnimationFrame(syncControls));
	}

	updateControls() {
		if (!this.flkty) {
			return;
		}

		const canScroll = this.flkty.slidesWidth > this.flkty.size.innerWidth + 1;
		const atStart = this.flkty.selectedIndex <= 0;
		const atEnd = this.flkty.selectedIndex >= this.flkty.slides.length - 1;

		this.DOM.prev?.toggleAttribute('disabled', !canScroll || atStart);
		this.DOM.next?.toggleAttribute('disabled', !canScroll || atEnd);

		if (!canScroll && this.flkty.selectedIndex !== 0) {
			this.flkty.select(0, false, true);
		}
	}

	events() {
		this.DOM.next?.addEventListener('click', () => {
			if (this.DOM.next?.hasAttribute('disabled')) {
				return;
			}

			this.flkty.next();
		});
		this.DOM.prev?.addEventListener('click', () => {
			if (this.DOM.prev?.hasAttribute('disabled')) {
				return;
			}

			this.flkty.previous();
		});
	}
}

new AltaXGen2PayloadsReady();

class AltaXGen2FlyAnythingTabs {
	DOM = {} as AltaXGen2FlyAnythingTabsDOM;
	desktopMediaQuery = window.matchMedia('(min-width: 1024px)');

	constructor() {
		this.cache();
		if (!this.DOM.root) {
			return;
		}

		this.syncBreakpoint();
		this.events();
		this.desktopMediaQuery.addEventListener('change', () => {
			this.syncBreakpoint();
		});
	}

	cache() {
		const root = document.querySelector('.js-altaXGen2FlyAnythingTabs') as HTMLElement | null;
		if (!root) {
			return;
		}

		this.DOM = {
			buttons: [...root.querySelectorAll('.js-tabButton')] as HTMLElement[],
			panels: [...root.querySelectorAll('.js-tabPanel')] as HTMLElement[],
			root,
		};
	}

	events() {
		this.DOM.buttons?.forEach((button, index) => {
			button.addEventListener('click', () => {
				this.activateTab(index);
			});
		});

		if (this.DOM.buttons?.length) {
			syncRovingTabindex(this.DOM.buttons, 0);
			bindTablistKeyboard(this.DOM.buttons, (index) => {
				this.activateTab(index);
			});
		}
	}

	syncBreakpoint() {
		if (!this.desktopMediaQuery.matches) {
			// Mobile stacks all panels; keep them available to AT and layout.
			this.DOM.panels?.forEach((panel) => {
				panel.classList.add('is-active');
				panel.setAttribute('aria-hidden', 'false');
			});
			return;
		}

		const activeIndex = Math.max(
			0,
			this.DOM.buttons?.findIndex((button) => button.classList.contains('is-active')) ?? 0
		);
		this.activateTab(activeIndex);
	}

	activateTab(index: number) {
		if (!this.desktopMediaQuery.matches) {
			return;
		}

		// Drives the sliding background rectangle behind the tab bar.
		this.DOM.root?.style.setProperty('--fly-tab-index', String(index));

		this.DOM.buttons?.forEach((button, buttonIndex) => {
			const isActive = buttonIndex === index;
			button.classList.toggle('is-active', isActive);
			button.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});

		if (this.DOM.buttons) {
			syncRovingTabindex(this.DOM.buttons, index);
		}

		// Panels stay stacked so the group keeps one height and can cross-fade; `is-active`
		// drives visibility rather than `hidden`, which cannot transition.
		this.DOM.panels?.forEach((panel, panelIndex) => {
			const isActive = panelIndex === index;
			panel.classList.toggle('is-active', isActive);
			panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
		});
	}
}

new AltaXGen2FlyAnythingTabs();

class AltaXGen2FlyAnythingSubtabs {
	constructor() {
		document.querySelectorAll('.js-flyAnythingSubtabs').forEach((group) => {
			this.initGroup(group as HTMLElement);
		});
	}

	initGroup(group: HTMLElement) {
		const buttons = [...group.querySelectorAll('.js-subtabButton')] as HTMLElement[];
		const panels = [...group.querySelectorAll('.js-subtabPanel')] as HTMLElement[];
		const thumbs = [...group.querySelectorAll('.js-subtabThumb')] as HTMLElement[];
		const buttonTrack = group.querySelector('.subtabButtons') as HTMLElement | null;

		buttons.forEach((button, index) => {
			button.addEventListener('click', () => {
				this.activateSubtab(buttons, panels, thumbs, index, buttonTrack);
			});
		});

		syncRovingTabindex(buttons, 0);
		bindTablistKeyboard(buttons, (index) => {
			this.activateSubtab(buttons, panels, thumbs, index, buttonTrack);
		});
	}

	activateSubtab(
		buttons: HTMLElement[],
		panels: HTMLElement[],
		thumbs: HTMLElement[],
		index: number,
		buttonTrack: HTMLElement | null = null
	) {
		buttonTrack?.style.setProperty('--fly-subtab-index', String(index));

		buttons.forEach((button, buttonIndex) => {
			const isActive = buttonIndex === index;
			button.classList.toggle('is-active', isActive);
			button.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});

		syncRovingTabindex(buttons, index);

		panels.forEach((panel, panelIndex) => {
			const isActive = panelIndex === index;
			panel.classList.toggle('is-active', isActive);

			if (isActive) {
				panel.removeAttribute('hidden');
			} else {
				panel.setAttribute('hidden', '');
			}
		});

		thumbs.forEach((thumb, thumbIndex) => {
			thumb.classList.toggle('is-active', thumbIndex === index);
		});
	}
}

new AltaXGen2FlyAnythingSubtabs();

const LBS_TO_KG = 0.45359237;
const MAX_PAYLOAD_LBS = 35;
const WEIGHT_STEP_LBS = 0.5;
// Finer than the +/- buttons so dragging the range input doesn't feel stepped.
const WEIGHT_SLIDER_STEP_LBS = 0.1;

const flightTimeMinutesFromMmSs = (mmSs: string) => {
	const [minutes, seconds] = mmSs.split(':').map(Number);
	return minutes + seconds / 60;
};

const FLIGHT_TIME_DATA = [
	{ payloadLbs: 0, minutes: flightTimeMinutesFromMmSs('40:02') },
	{ payloadLbs: 5, minutes: flightTimeMinutesFromMmSs('34:28') },
	{ payloadLbs: 10, minutes: flightTimeMinutesFromMmSs('29:40') },
	{ payloadLbs: 15, minutes: flightTimeMinutesFromMmSs('25:37') },
	{ payloadLbs: 20, minutes: flightTimeMinutesFromMmSs('22:19') },
	{ payloadLbs: 25, minutes: flightTimeMinutesFromMmSs('19:48') },
	{ payloadLbs: 30, minutes: flightTimeMinutesFromMmSs('18:01') },
	{ payloadLbs: 35, minutes: flightTimeMinutesFromMmSs('17:00') },
] as const;

const flightTimeFromPayloadLbs = (payloadLbs: number) => {
	const weight = Math.max(0, Math.min(MAX_PAYLOAD_LBS, payloadLbs));
	const points = FLIGHT_TIME_DATA;

	if (weight <= points[0].payloadLbs) {
		return points[0].minutes;
	}

	if (weight >= points[points.length - 1].payloadLbs) {
		return points[points.length - 1].minutes;
	}

	for (let index = 0; index < points.length - 1; index += 1) {
		const start = points[index];
		const end = points[index + 1];

		if (weight >= start.payloadLbs && weight <= end.payloadLbs) {
			const progress = (weight - start.payloadLbs) / (end.payloadLbs - start.payloadLbs);
			return start.minutes + progress * (end.minutes - start.minutes);
		}
	}

	return points[points.length - 1].minutes;
};

class AltaXGen2FlightTime {
	DOM = {} as AltaXGen2FlightTimeDOM;
	chart = {
		gridStroke: 0.75,
		height: 248,
		width: 448,
		xMax: 35,
		yMax: 40,
	};
	chartSized = false;
	resizeObserver: ResizeObserver | null = null;
	stepHoldDelay = 350;
	stepHoldInterval = 75;
	unit: 'kgs' | 'lbs' = 'lbs';
	weightLbs = 15;

	constructor() {
		this.cache();
		if (!this.DOM.root) {
			return;
		}

		this.events();
		requestAnimationFrame(() => {
			this.syncChartSize();
		});
	}

	cache() {
		const root = document.querySelector('.js-altaXGen2FlightTime') as HTMLElement | null;
		if (!root) {
			return;
		}

		this.DOM = {
			chart: root.querySelector('.js-flightChart') as SVGSVGElement | null,
			chartAxisLabelsX: [
				...root.querySelectorAll('.js-chartAxisLabelsX .chartAxisLabel'),
			] as HTMLElement[],
			chartAxisX: root.querySelector('.js-chartAxisX') as HTMLElement | null,
			chartPlot: root.querySelector('.js-chartPlot') as HTMLElement | null,
			chartPoint: root.querySelector('.js-chartPoint') as HTMLElement | null,
			chartTooltip: root.querySelector('.js-chartTooltip') as HTMLElement | null,
			chartTooltipValue: root.querySelector('.js-chartTooltipValue') as HTMLElement | null,
			chartWrap: root.querySelector('.chartWrap') as HTMLElement | null,
			flightStatus: root.querySelector('.js-flightStatus') as HTMLElement | null,
			flightTimeValue: root.querySelector('.js-flightTimeValue') as HTMLElement | null,
			payloadSlider: root.querySelector('.js-payloadSlider') as HTMLInputElement | null,
			payloadUnit: root.querySelector('.js-payloadUnit') as HTMLElement | null,
			payloadValue: root.querySelector('.js-payloadValue') as HTMLElement | null,
			root,
			sliderMaxUnit: root.querySelector('.js-sliderMaxUnit') as HTMLElement | null,
			sliderMaxValue: root.querySelector('.js-sliderMaxValue') as HTMLElement | null,
			sliderMinUnit: root.querySelector('.js-sliderMinUnit') as HTMLElement | null,
			sliderMinValue: root.querySelector('.js-sliderMinValue') as HTMLElement | null,
			stepDown: root.querySelector('.js-stepDown') as HTMLElement | null,
			stepUp: root.querySelector('.js-stepUp') as HTMLElement | null,
			unitButtons: [...root.querySelectorAll('.js-unitButton')] as HTMLButtonElement[],
		};
	}

	events() {
		this.DOM.payloadSlider?.addEventListener('input', () => {
			this.setWeightLbs(this.getSliderWeightLbs());
		});

		this.bindStepButton(this.DOM.stepDown, -WEIGHT_STEP_LBS);
		this.bindStepButton(this.DOM.stepUp, WEIGHT_STEP_LBS);

		this.DOM.unitButtons?.forEach((button) => {
			button.addEventListener('click', () => {
				const unit = button.dataset.unit as 'kgs' | 'lbs';
				if (unit && unit !== this.unit) {
					this.setUnit(unit);
				}
			});
		});

		window.addEventListener('resize', () => {
			this.syncChartSize();
		});

		if (this.DOM.chart) {
			this.resizeObserver = new ResizeObserver(() => {
				this.syncChartSize();
			});
			this.resizeObserver.observe(this.DOM.chart);
		}
	}

	bindStepButton(button: HTMLElement | null, delta: number) {
		if (!button) {
			return;
		}

		let delayId: number | undefined;
		let repeatId: number | undefined;
		let ignoreClick = false;

		const adjust = () => {
			this.setWeightLbs(this.stepWeightFromButtons(delta));
		};

		const stop = () => {
			window.clearTimeout(delayId);
			window.clearInterval(repeatId);
			delayId = undefined;
			repeatId = undefined;
		};

		const start = () => {
			stop();
			adjust();
			ignoreClick = true;
			delayId = window.setTimeout(() => {
				repeatId = window.setInterval(adjust, this.stepHoldInterval);
			}, this.stepHoldDelay);
		};

		button.addEventListener('pointerdown', (event) => {
			if (event.button !== 0) {
				return;
			}

			event.preventDefault();
			button.setPointerCapture(event.pointerId);
			start();
		});

		const end = (event: PointerEvent) => {
			if (button.hasPointerCapture(event.pointerId)) {
				button.releasePointerCapture(event.pointerId);
			}

			stop();
		};

		button.addEventListener('pointerup', end);
		button.addEventListener('pointercancel', end);
		button.addEventListener('lostpointercapture', stop);

		button.addEventListener('click', (event) => {
			if (ignoreClick) {
				ignoreClick = false;
				event.preventDefault();
				return;
			}

			adjust();
		});
	}

	syncChartSize() {
		const chart = this.DOM.chart;
		if (!chart) {
			return;
		}

		const { width, height } = chart.getBoundingClientRect();
		if (!width || !height) {
			return;
		}

		const nextWidth = Math.round((width / height) * this.chart.height);
		if (nextWidth === this.chart.width && this.chartSized) {
			this.positionChartPoint(flightTimeFromPayloadLbs(this.weightLbs));
			this.positionTooltip();
			return;
		}

		this.chartSized = true;
		this.chart.width = Math.max(nextWidth, 1);
		this.setChartViewBox();
		this.renderChart();
		this.update();
	}

	getViewBoxPad() {
		return this.chart.gridStroke / 2;
	}

	getViewBoxWidth() {
		return this.chart.width + this.getViewBoxPad() * 2;
	}

	getViewBoxHeight() {
		return this.chart.height + this.getViewBoxPad() * 2;
	}

	setChartViewBox() {
		const chart = this.DOM.chart;
		if (!chart) {
			return;
		}

		const pad = this.getViewBoxPad();
		chart.setAttribute(
			'viewBox',
			`${-pad} ${-pad} ${this.getViewBoxWidth()} ${this.getViewBoxHeight()}`
		);
	}

	getMaxDisplayWeight() {
		return this.unit === 'lbs' ? MAX_PAYLOAD_LBS : MAX_PAYLOAD_LBS * LBS_TO_KG;
	}

	getStepDisplayWeight() {
		return this.unit === 'lbs' ? WEIGHT_STEP_LBS : WEIGHT_STEP_LBS * LBS_TO_KG;
	}

	getSliderStepDisplayWeight() {
		return this.unit === 'lbs' ? WEIGHT_SLIDER_STEP_LBS : WEIGHT_SLIDER_STEP_LBS * LBS_TO_KG;
	}

	// Buttons always land on the 0.5 lb grid. After a fine drag (e.g. 15.3), up goes to
	// 15.5 and down to 15.0 — never 15.8 / 14.8.
	stepWeightFromButtons(delta: number) {
		const step = WEIGHT_STEP_LBS;
		const weight = this.weightLbs;

		if (delta > 0) {
			return Math.floor(weight / step + 1e-9) * step + step;
		}

		return Math.ceil(weight / step - 1e-9) * step - step;
	}

	getSliderWeightLbs() {
		const slider = this.DOM.payloadSlider;
		if (!slider) {
			return this.weightLbs;
		}

		const value = Number(slider.value);
		return this.unit === 'lbs' ? value : value / LBS_TO_KG;
	}

	setUnit(unit: 'kgs' | 'lbs') {
		this.unit = unit;
		this.DOM.unitButtons?.forEach((button) => {
			button.classList.toggle('is-active', button.dataset.unit === unit);
		});
		this.syncSlider();
		this.update();
	}

	setWeightLbs(weightLbs: number) {
		this.weightLbs = Math.max(0, Math.min(MAX_PAYLOAD_LBS, weightLbs));
		this.syncSlider();
		this.update();
	}

	syncSlider() {
		const slider = this.DOM.payloadSlider;
		if (!slider) {
			return;
		}

		const max = this.getMaxDisplayWeight();
		const step = this.getSliderStepDisplayWeight();
		const value = this.unit === 'lbs' ? this.weightLbs : this.weightLbs * LBS_TO_KG;

		slider.min = '0';
		slider.max = String(max);
		slider.step = String(step);
		slider.value = String(value);
		slider.setAttribute('aria-valuemin', '0');
		slider.setAttribute('aria-valuemax', String(max));
		slider.setAttribute('aria-valuenow', String(value));
	}

	formatWeight(weightLbs: number) {
		const value = this.unit === 'lbs' ? weightLbs : weightLbs * LBS_TO_KG;
		return value.toFixed(1);
	}

	formatFlightTime(minutes: number) {
		return minutes.toFixed(2);
	}

	getChartPointPosition(flightMinutes: number) {
		const wrap = this.DOM.chartWrap;
		const chart = this.DOM.chart;
		if (!wrap || !chart) {
			return null;
		}

		const svgPoint = chart.createSVGPoint();
		svgPoint.x = this.toChartX(this.weightLbs);
		svgPoint.y = this.toChartY(flightMinutes);

		const ctm = chart.getScreenCTM();
		if (!ctm) {
			return null;
		}

		const screenPoint = svgPoint.matrixTransform(ctm);
		const wrapRect = wrap.getBoundingClientRect();

		return {
			left: screenPoint.x - wrapRect.left,
			top: screenPoint.y - wrapRect.top,
		};
	}

	positionChartPoint(flightMinutes: number) {
		const point = this.DOM.chartPoint;
		const position = this.getChartPointPosition(flightMinutes);
		if (!point || !position) {
			return;
		}

		point.style.left = `${position.left}px`;
		point.style.top = `${position.top}px`;
	}

	toChartX(payloadLbs: number) {
		return (payloadLbs / this.chart.xMax) * this.chart.width;
	}

	toChartY(flightMinutes: number) {
		return this.chart.height - (flightMinutes / this.chart.yMax) * this.chart.height;
	}

	renderChart() {
		const plot = this.DOM.chartPlot;
		if (!plot) {
			return;
		}

		const { height, width, xMax, yMax } = this.chart;
		const gridLines: string[] = [];

		for (let payload = 0; payload <= xMax; payload += 5) {
			const x = this.toChartX(payload);
			gridLines.push(`<line x1="${x}" y1="0" x2="${x}" y2="${height}" />`);
		}

		for (let minutes = 0; minutes <= yMax; minutes += 5) {
			const y = this.toChartY(minutes);
			const isMajor = minutes % 10 === 0;
			const className = isMajor ? 'chartGridMajor' : 'chartGridMinor';
			gridLines.push(`<line class="${className}" x1="0" y1="${y}" x2="${width}" y2="${y}" />`);
		}

		const curvePoints: string[] = [];
		for (let payload = 0; payload <= xMax; payload += 0.5) {
			const x = this.toChartX(payload);
			const y = this.toChartY(flightTimeFromPayloadLbs(payload));
			curvePoints.push(`${x.toFixed(2)},${y.toFixed(2)}`);
		}

		plot.innerHTML = `
			<g class="chartGrid">${gridLines.join('')}</g>
			<polyline class="chartCurve js-chartCurve" vector-effect="non-scaling-stroke" points="${curvePoints.join(' ')}" />
		`;
	}

	updateChartLabels() {
		const unitLabel = this.unit === 'lbs' ? 'LBS' : 'KGS';

		if (this.DOM.chartAxisX) {
			this.DOM.chartAxisX.textContent = `PAYLOAD (${unitLabel})`;
		}

		this.DOM.chartAxisLabelsX.forEach((label, index) => {
			const payload = index * 5;
			const displayValue = this.unit === 'lbs' ? payload : Number((payload * LBS_TO_KG).toFixed(1));
			label.textContent = String(displayValue);
		});

		this.DOM.sliderMinValue.textContent = '0';
		this.DOM.sliderMinUnit.textContent = unitLabel;
		this.DOM.sliderMaxValue.textContent = this.formatWeight(MAX_PAYLOAD_LBS);
		this.DOM.sliderMaxUnit.textContent = unitLabel;
	}

	positionTooltip() {
		const tooltip = this.DOM.chartTooltip;
		const flightTime = flightTimeFromPayloadLbs(this.weightLbs);
		const position = this.getChartPointPosition(flightTime);
		if (!tooltip || !position) {
			return;
		}

		tooltip.style.left = `${position.left}px`;
		tooltip.style.top = `${position.top}px`;
	}

	update() {
		const flightTime = flightTimeFromPayloadLbs(this.weightLbs);
		const unitLabel = this.unit === 'lbs' ? 'LBS' : 'KGS';
		const weightLabel = this.formatWeight(this.weightLbs);
		const timeLabel = this.formatFlightTime(flightTime);

		this.DOM.payloadValue.textContent = weightLabel;
		this.DOM.payloadUnit.textContent = unitLabel;
		this.DOM.flightTimeValue.textContent = timeLabel;
		this.DOM.chartTooltipValue.textContent = timeLabel;

		if (this.DOM.flightStatus) {
			this.DOM.flightStatus.textContent = `Payload ${weightLabel} ${unitLabel}, flight time ${timeLabel} minutes`;
		}

		this.positionChartPoint(flightTime);
		this.updateChartLabels();
		this.positionTooltip();
	}
}

new AltaXGen2FlightTime();

class AltaXGen2Features {
	DOM = {} as AltaXGen2FeaturesDOM;
	activeIndex = 0;
	expandedMobileTile: HTMLElement | null = null;
	prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
	resizeObserver: ResizeObserver | null = null;
	stageDesignWidth = 1335;
	stageMediaQuery = window.matchMedia('(min-width: 1024px)');

	constructor() {
		this.cache();
		if (!this.DOM.root || !this.DOM.buttons?.length) {
			return;
		}

		this.events();
		this.initMobileAccordion();
		this.updateStageScale();
		this.activate(0);
		window.addEventListener('load', () => {
			this.updateStageScale();
			this.positionTabConnector();
		});
		requestAnimationFrame(() => {
			this.updateStageScale();
			this.positionTabConnector();
		});
	}

	cache() {
		const root = document.querySelector('.js-altaXGen2Features') as HTMLElement | null;
		if (!root) {
			return;
		}

		this.DOM = {
			buttons: [...root.querySelectorAll('.js-featureButton')] as HTMLButtonElement[],
			captionVideos: [...root.querySelectorAll('.js-featureCaptionVideo')] as HTMLVideoElement[],
			heroVideos: [...root.querySelectorAll('.js-featureHeroVideo')] as HTMLVideoElement[],
			heroWrap: root.querySelector('.js-featureHeroWrap') as HTMLElement | null,
			mobileTiles: [...root.querySelectorAll('.js-featureMobileTile')] as HTMLElement[],
			overlays: [...root.querySelectorAll('.js-featureOverlay')] as HTMLElement[],
			panels: [...root.querySelectorAll('.js-featurePanel')] as HTMLElement[],
			root,
			stage: root.querySelector('.js-featureStage') as HTMLElement | null,
			stageViewport: root.querySelector('.js-featureStageViewport') as HTMLElement | null,
			tabConnector: root.querySelector('.js-featureTabConnector') as SVGSVGElement | null,
			tabConnectorPath: root.querySelector('.js-featureTabConnectorPath') as SVGPathElement | null,
		};
	}

	initMobileAccordion() {
		this.DOM.mobileTiles?.forEach((tile) => {
			const toggle = tile.querySelector('.js-featureMobileToggle') as HTMLButtonElement | null;
			toggle?.addEventListener('click', () => {
				this.toggleMobileTile(tile);
			});
		});
	}

	closeAllMobileTiles() {
		this.DOM.mobileTiles?.forEach((tile) => {
			this.setMobileTileExpanded(tile, false);
		});
	}

	toggleMobileTile(tile: HTMLElement) {
		const isExpanded = tile.classList.contains('is-expanded');

		this.closeAllMobileTiles();

		if (!isExpanded) {
			this.setMobileTileExpanded(tile, true);
		}
	}

	setMobileTileExpanded(tile: HTMLElement, expanded: boolean) {
		const toggle = tile.querySelector('.js-featureMobileToggle') as HTMLButtonElement | null;

		tile.classList.toggle('is-expanded', expanded);
		toggle?.setAttribute('aria-expanded', expanded ? 'true' : 'false');

		this.expandedMobileTile = expanded ? tile : null;
		this.syncCaptionVideos();
	}

	shouldPlayCaptionVideos() {
		return !this.prefersReducedMotion.matches;
	}

	syncCaptionVideos() {
		const isDesktop = this.stageMediaQuery.matches;
		const shouldPlay = this.shouldPlayCaptionVideos();

		this.DOM.captionVideos?.forEach((video) => {
			const inDesktopPanel = Boolean(video.closest('.js-featurePanel'));
			const inMobileCaption = Boolean(video.closest('.js-featureMobileCaption'));
			let play = false;

			if (isDesktop && inDesktopPanel) {
				const panel = video.closest('.js-featurePanel') as HTMLElement | null;
				const panelIndex = panel ? (this.DOM.panels?.indexOf(panel) ?? -1) : -1;
				play = panelIndex === this.activeIndex;
			} else if (!isDesktop && inMobileCaption) {
				play = Boolean(this.expandedMobileTile?.contains(video));
			}

			if (play && shouldPlay) {
				video.play().catch(() => {
					// Autoplay may be blocked until user interaction.
				});
				return;
			}

			video.pause();
		});

		this.syncHeroVideos();
	}

	syncHeroVideos() {
		const isDesktop = this.stageMediaQuery.matches;
		const shouldPlay = this.shouldPlayCaptionVideos();

		this.DOM.heroVideos?.forEach((video) => {
			const videoIndex = Number(video.dataset.featureIndex || -1);
			const isActive = videoIndex === this.activeIndex;

			video.classList.toggle('is-active', isActive);

			if (isActive && isDesktop && shouldPlay) {
				video.play().catch(() => {
					// Autoplay may be blocked until user interaction.
				});
				return;
			}

			video.pause();
		});
	}

	events() {
		this.DOM.buttons?.forEach((button, index) => {
			button.addEventListener('click', () => {
				this.activate(index);
			});
		});

		if (this.DOM.buttons?.length) {
			bindTablistKeyboard(this.DOM.buttons, (index) => {
				this.activate(index);
			});
		}

		window.addEventListener('resize', () => {
			this.updateStageScale();
			this.positionTabConnector();
		});

		this.stageMediaQuery.addEventListener('change', () => {
			this.updateStageScale();
			this.positionTabConnector();
			this.syncCaptionVideos();
		});

		this.prefersReducedMotion.addEventListener('change', () => {
			this.syncCaptionVideos();
		});

		if (this.DOM.stageViewport) {
			this.resizeObserver = new ResizeObserver(() => {
				this.updateStageScale();
				this.positionTabConnector();
			});
			this.resizeObserver.observe(this.DOM.stageViewport);
		}
	}

	updateStageScale() {
		const stage = this.DOM.stage;
		const viewport = this.DOM.stageViewport;
		if (!stage || !viewport) {
			return;
		}

		if (this.stageMediaQuery.matches) {
			const availableWidth = viewport.clientWidth;
			if (!availableWidth) {
				return;
			}

			const scale = availableWidth / this.stageDesignWidth;
			stage.style.transform = `scale(${scale})`;
			stage.style.transformOrigin = 'top left';
			return;
		}

		stage.style.removeProperty('transform');
		stage.style.removeProperty('transform-origin');
	}

	activate(index: number) {
		const button = this.DOM.buttons?.[index];
		if (!button) {
			return;
		}

		this.activeIndex = index;

		this.DOM.buttons?.forEach((item, itemIndex) => {
			const isActive = itemIndex === index;
			item.classList.toggle('is-active', isActive);
			item.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});

		if (this.DOM.buttons) {
			syncRovingTabindex(this.DOM.buttons, index);
		}

		this.DOM.overlays?.forEach((overlay) => {
			const overlayIndex = Number(overlay.dataset.featureIndex || -1);
			const isActive = overlayIndex === index;

			overlay.classList.toggle('is-active', isActive);

			if (isActive) {
				overlay.removeAttribute('hidden');
			} else {
				overlay.setAttribute('hidden', '');
			}
		});

		// Panels are stacked and cross-fade via `is-active`; `hidden` would force
		// display:none and cut the transition short.
		this.DOM.panels?.forEach((panel, panelIndex) => {
			const isActive = panelIndex === index;
			panel.classList.toggle('is-active', isActive);
			panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
		});

		this.positionTabConnector();
		this.syncCaptionVideos();
	}

	positionTabConnector() {
		if (!this.stageMediaQuery.matches) {
			return;
		}

		const stage = this.DOM.stage;
		const heroWrap = this.DOM.heroWrap;
		const connector = this.DOM.tabConnector;
		const path = this.DOM.tabConnectorPath;
		const button = this.DOM.buttons?.[this.activeIndex];
		const indicator = button?.querySelector('.js-featureIndicator') as HTMLElement | null;

		if (!stage || !heroWrap || !connector || !path || !indicator) {
			return;
		}

		const stageRect = stage.getBoundingClientRect();
		const heroRect = heroWrap.getBoundingClientRect();
		const indicatorRect = indicator.getBoundingClientRect();

		if (!stageRect.width || !stageRect.height) {
			return;
		}

		const startX = indicatorRect.left + indicatorRect.width / 2 - stageRect.left;
		const startY = indicatorRect.top + indicatorRect.height / 2 - stageRect.top;
		const endX = heroRect.left - stageRect.left;
		const anchorY = button?.dataset.anchorY
			? (parseFloat(button.dataset.anchorY) / 100) * heroRect.height + heroRect.top - stageRect.top
			: startY;

		let d = `M ${startX.toFixed(2)} ${startY.toFixed(2)} H ${endX.toFixed(2)}`;

		if (Math.abs(anchorY - startY) > 1) {
			d += ` V ${anchorY.toFixed(2)}`;
		}

		connector.setAttribute('viewBox', `0 0 ${stageRect.width} ${stageRect.height}`);
		connector.setAttribute('width', String(stageRect.width));
		connector.setAttribute('height', String(stageRect.height));
		path.setAttribute('d', d);
	}
}

new AltaXGen2Features();

class AltaXGen2Testing {
	DOM = {} as AltaXGen2TestingDOM;
	prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
	desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
	videoObserver: IntersectionObserver | null = null;

	constructor() {
		this.cache();
		if (!this.DOM.root) {
			return;
		}

		this.events();
		this.initFeatureVideo();
	}

	cache() {
		const root = document.querySelector('.js-altaXGen2Testing') as HTMLElement | null;
		if (!root) {
			return;
		}

		this.DOM = {
			cards: [...root.querySelectorAll('.js-testCard.has-details')] as HTMLElement[],
			featureVideo: root.querySelector('.js-testingFeatureVideo') as HTMLVideoElement | null,
			root,
		};
	}

	events() {
		this.DOM.cards?.forEach((card) => {
			const toggle = card.querySelector('.js-testToggle') as HTMLButtonElement | null;
			toggle?.addEventListener('click', () => {
				this.toggleCard(card);
			});
		});
	}

	initFeatureVideo() {
		const video = this.DOM.featureVideo;
		const src = video?.dataset.src;

		if (!video || !src || this.prefersReducedMotion.matches || !this.desktopMediaQuery.matches) {
			return;
		}

		if (!('IntersectionObserver' in window)) {
			this.loadAndPlayFeatureVideo(video, src);
			return;
		}

		this.videoObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						video.pause();
						return;
					}

					this.loadAndPlayFeatureVideo(video, src);
				});
			},
			{ rootMargin: '10% 0px', threshold: 0.2 }
		);

		this.videoObserver.observe(video);
	}

	loadAndPlayFeatureVideo(video: HTMLVideoElement, src: string) {
		if (video.dataset.loaded !== 'true') {
			video.dataset.loaded = 'true';
			video.src = src;
			video.load();
		}

		const play = () => {
			video.play().catch(() => {
				// Autoplay may be blocked; poster remains visible.
			});
		};

		if (video.readyState >= 2) {
			play();
			return;
		}

		video.addEventListener('loadeddata', play, { once: true });
	}

	closeAll() {
		this.DOM.cards?.forEach((card) => {
			this.setCardExpanded(card, false);
		});
	}

	toggleCard(card: HTMLElement) {
		const isExpanded = card.classList.contains('is-expanded');

		this.closeAll();

		if (!isExpanded) {
			this.setCardExpanded(card, true);
		}
	}

	setCardExpanded(card: HTMLElement, expanded: boolean) {
		const toggle = card.querySelector('.js-testToggle') as HTMLButtonElement | null;
		const icon = card.querySelector('.js-testToggleIcon') as HTMLElement | null;

		card.classList.toggle('is-expanded', expanded);
		toggle?.setAttribute('aria-expanded', expanded ? 'true' : 'false');

		if (icon) {
			icon.textContent = expanded ? '−' : '+';
		}
	}
}

new AltaXGen2Testing();

class AltaXGen2Reveal {
	observer: IntersectionObserver | null = null;

	constructor() {
		const targets = [...document.querySelectorAll('.js-reveal')] as HTMLElement[];
		if (!targets.length) {
			return;
		}

		document.documentElement.classList.add('has-altaXGen2Reveal');

		if (!('IntersectionObserver' in window)) {
			targets.forEach((target) => target.classList.add('is-inview'));
			return;
		}

		this.observer = new IntersectionObserver(
			(entries, observer) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						return;
					}

					entry.target.classList.add('is-inview');
					observer.unobserve(entry.target);
				});
			},
			{ rootMargin: '0px 0px -10% 0px' }
		);

		targets.forEach((target) => {
			// Anything already on screen when the bundle runs is shown outright, so
			// it never paints visible and then snaps back to the hidden state.
			if (this.isOnScreen(target)) {
				target.classList.add('is-inview');
				return;
			}

			const { revealDelay, revealDuration } = target.dataset;
			if (revealDelay) {
				target.style.setProperty('--reveal-delay', `${revealDelay}s`);
			}

			if (revealDuration) {
				target.style.setProperty('--reveal-duration', `${revealDuration}s`);
			}

			this.observer?.observe(target);
		});
	}

	isOnScreen(target: HTMLElement) {
		const rect = target.getBoundingClientRect();

		return rect.top < window.innerHeight && rect.bottom > 0;
	}
}

new AltaXGen2Reveal();

/**
 * Keep the hero still image as LCP: attach/play the loop only after load + idle,
 * and skip entirely when the user prefers reduced motion.
 * Desktop (>=768px) gets 1080p; mobile gets 720p (may revisit framing later).
 */
class AltaXGen2HeroVideo {
	constructor() {
		const video = document.querySelector('.js-altaXGen2HeroVideo') as HTMLVideoElement | null;
		const srcDesktop = video?.dataset.srcDesktop;
		const srcMobile = video?.dataset.srcMobile;
		const src = window.matchMedia('(min-width: 768px)').matches ? srcDesktop : srcMobile;

		if (!video || !src) {
			return;
		}

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			return;
		}

		const start = () => {
			if (video.dataset.loaded === 'true') {
				return;
			}

			video.dataset.loaded = 'true';
			video.src = src;
			video.load();

			const play = () => {
				video.play().catch(() => {
					// Autoplay can still be blocked; first-frame poster remains visible.
				});
			};

			if (video.readyState >= 2) {
				play();
				return;
			}

			video.addEventListener('loadeddata', play, { once: true });
		};

		const schedule = () => {
			const idle = (
				window as Window & {
					requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
				}
			).requestIdleCallback;

			if (typeof idle === 'function') {
				idle(start, { timeout: 2500 });
				return;
			}

			window.setTimeout(start, 1500);
		};

		if (document.readyState === 'complete') {
			schedule();
			return;
		}

		window.addEventListener('load', schedule, { once: true });
	}
}

new AltaXGen2HeroVideo();
